import { useMemo } from 'react';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { useLocationGroupStore } from '@frontend/scope';
import { useWorkspaceFeature } from '@frontend/workspace-scope';
import {
  MUTED_INBOX_CAPABILITIES,
  MUTED_INBOX_DISABLED_CAPABILITIES,
  MUTED_CENTER_NOTIFICATION_TYPES,
  type InboxMutedCapability,
} from './registry';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';

/**
 * Inbox mute policy backed by the dedicated MuteInboxLocationSetting.
 *
 * The mute inbox setting only exists in the context of workspaces:
 * - Wetag disabled → query is skipped, isMuted is always false
 * - Wetag enabled + workspace mode → isMuted is always false (workspace overrides)
 * - Wetag enabled + NOT workspace mode → isMuted depends on location settings
 */
export const useInboxMutePolicy = () => {
  const selectedLocationIds = useLocationGroupStore((state) => state.selectedLocationIds);
  const selectedOrgId = useLocationGroupStore((state) => state.selectedOrgId);
  const { isWorkspaceMode, isEnabled: isWetagEnabled, isWorkspaceAdmin } = useWorkspaceFeature();

  const normalizedSelectedLocationIds = useMemo(
    () => Array.from(new Set(selectedLocationIds)).sort(),
    [selectedLocationIds]
  );

  // Only query mute settings when the workspace wetag is enabled.
  // If wetag is off, no location will ever have a mute setting — skip the request entirely.
  const { data: mutedFromList } = MessagingSettingsV1.Queries.useListQuery(
    {
      settingType: SettingType_Enum.MUTE_INBOX_LOCATION_SETTING,
      locationIds: normalizedSelectedLocationIds,
      orgId: selectedOrgId,
    },
    {
      enabled: normalizedSelectedLocationIds.length > 0 && isWetagEnabled,
      select: (data) => {
        return normalizedSelectedLocationIds.reduce<Record<string, boolean | undefined>>((acc, currentLocationId) => {
          const locationSetting = data.settings.find((setting) => setting.locationId === currentLocationId);
          acc[currentLocationId] =
            locationSetting?.setting && 'muteInboxLocationSetting' in locationSetting.setting
              ? locationSetting.setting.muteInboxLocationSetting.muted
              : undefined;
          return acc;
        }, {});
      },
    }
  );

  const isMuted =
    !isWorkspaceMode &&
    normalizedSelectedLocationIds.length > 0 &&
    normalizedSelectedLocationIds.some((currentLocationId) => mutedFromList?.[currentLocationId] === true);

  return useMemo(
    () => ({
      isMuted,

      isHidden: (capability: InboxMutedCapability) => {
        if (capability === 'settings-messaging' && isWorkspaceAdmin) return false;
        return isMuted && MUTED_INBOX_CAPABILITIES.has(capability);
      },

      isDisabled: (capability: InboxMutedCapability) => {
        if (capability === 'settings-org-workspaces' && isWorkspaceAdmin) return false;
        return isMuted && MUTED_INBOX_DISABLED_CAPABILITIES.has(capability);
      },

      canShowInNotificationCenter: (notificationType: NotificationType): boolean => {
        if (!isMuted) return true;
        return !MUTED_CENTER_NOTIFICATION_TYPES.has(notificationType);
      },
    }),
    [isMuted, isWorkspaceAdmin]
  );
};
